import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiRequest } from "@/lib/queryClient";

interface InvitationData {
  email: string;
  role: string;
  companyName: string;
}

export default function TeamInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const [_, setLocation] = useLocation();
  const { toast } = useToast();
  
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Neplatný invitation token');
        setLoading(false);
        return;
      }

      try {
        const response = await apiRequest("GET", `/api/team-invitation/${token}`);
        const data = await response.json();
        setInvitationData(data);
      } catch (error: any) {
        setError(error.message || 'Nepodarilo sa validovať pozvánku');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Informácia",
        description: "Heslá sa nezhodujú",
        variant: "default",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Informácia", 
        description: "Heslo musí mať aspoň 6 znakov",
        variant: "default",
      });
      return;
    }

    setRegistering(true);

    try {
      const response = await apiRequest("POST", `/api/team-invitation/${token}/register`, {
        username: formData.username,
        password: formData.password,
        companyName: formData.companyName || invitationData?.companyName
      });

      const result = await response.json();
      
      toast({
        title: "Úspech!",
        description: "Registrácia bola úspešná. Presmerovávame vás...",
      });

      // Presmeruj na hlavnú stránku po úspešnej registrácii
      setTimeout(() => {
        setLocation('/');
      }, 2000);

    } catch (error: any) {
      toast({
        title: "Chyba registrácie",
        description: error.message || 'Nastala chyba pri registrácii',
        variant: "default",
      });
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Chyba
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button 
              onClick={() => setLocation('/auth')} 
              className="w-full mt-4"
              variant="outline"
            >
              Späť na prihlásenie
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Pozvánka do tímu
          </CardTitle>
          <CardDescription>
            Dokončite svoju registráciu do tímu
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {invitationData && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">Detaily pozvánky:</h3>
              <p className="text-sm text-blue-700"><strong>Email:</strong> {invitationData.email}</p>
              <p className="text-sm text-blue-700"><strong>Pozícia:</strong> {invitationData.role}</p>
              <p className="text-sm text-blue-700"><strong>Spoločnosť:</strong> {invitationData.companyName}</p>
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="username">Používateľské meno</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                placeholder="Vaše používateľské meno"
              />
            </div>

            <div>
              <Label htmlFor="password">Heslo</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="Minimálne 6 znakov"
                minLength={6}
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword">Potvrdiť heslo</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                placeholder="Zopakujte heslo"
              />
            </div>

            <div>
              <Label htmlFor="companyName">Názov spoločnosti (voliteľné)</Label>
              <Input
                id="companyName"
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                placeholder={invitationData?.companyName || "Názov vašej spoločnosti"}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={registering}
            >
              {registering ? "Registrujem..." : "Dokončiť registráciu"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Button 
              variant="ghost" 
              onClick={() => setLocation('/auth')}
              className="text-sm"
            >
              Už máte účet? Prihlásiť sa
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}